import type {
	CategoryModel,
	GetProductGroupsParams,
	OptimizationGroupModel,
	ProductGroupModel,
	SalesEntitiesResponseModel,
	TreeMapProductGroupModel,
	TreeMapRequestModel,
	TreeMapSalesEntityModel,
} from '@/shared/generated'
import type { UseQueryOptions } from '@/shared/lib/query-client'

import { useQuery } from '@tanstack/react-query'

import { client } from '@/shared/api/client'

export const optimizationGroupQueries = {
	single: (optimizationGroupId: string) => ({
		queryKey: ['optimization-groups', optimizationGroupId],
		queryFn: () =>
			client.optimizationGroups.getOptimizationGroup({ optimizationGroupId }),
	}),
	list: () => ({
		queryKey: ['optimization-groups'],
		queryFn: () => client.optimizationGroups.getOptimizationGroups(),
	}),
	salesPoints: () => ({
		queryKey: ['sales-points'],
		queryFn: () => client.optimizationGroups.getSalesEntities(),
	}),
	productGroups: (params: GetProductGroupsParams) => ({
		queryKey: ['product-groups', params],
		queryFn: () => client.optimizationGroups.getProductGroups(params),
	}),
	treeMapByProductGroups: (params: TreeMapRequestModel) => ({
		queryKey: ['og-tree-map-product-groups', params],
		queryFn: () => client.bi.getOgTreeMapByProductGroups(params),
	}),
	treeMapBySalesEntities: (params: TreeMapRequestModel) => ({
		queryKey: ['og-tree-map-sales-entities', params],
		queryFn: () => client.bi.getOgTreeMapBySalesEntities(params),
	}),
	dryCategories: () => ({
		queryKey: ['dry-categories'],
		queryFn: () => client.products.getCategories(),
	}),
}
export function useOptimizationGroupsQuery<T = OptimizationGroupModel[]>(
	options?: UseQueryOptions<OptimizationGroupModel[], Error, T>
) {
	return useQuery<OptimizationGroupModel[], Error, T>({
		...optimizationGroupQueries.list(),
		...(options || {}),
	})
}
export function useOptimizationGroupQuery<T = OptimizationGroupModel>(
	optimizationGroupId: string,
	options?: UseQueryOptions<OptimizationGroupModel, Error, T>
) {
	return useQuery<OptimizationGroupModel, Error, T>({
		...optimizationGroupQueries.single(optimizationGroupId),
		...(options || {}),
	})
}
export function useSalesEntitiesQuery<T = SalesEntitiesResponseModel>(
	options?: UseQueryOptions<SalesEntitiesResponseModel, Error, T>
) {
	return useQuery<SalesEntitiesResponseModel, Error, T>({
		...optimizationGroupQueries.salesPoints(),
		...(options || {}),
		staleTime: 60 * 1000 * 60,
	})
}
export function useProductGroupsQuery<T = ProductGroupModel[]>(
	params: GetProductGroupsParams,
	options?: UseQueryOptions<ProductGroupModel[], Error, T>
) {
	return useQuery<ProductGroupModel[], Error, T>({
		...optimizationGroupQueries.productGroups(params),
		...(options || {}),
		staleTime: 60 * 1000 * 60,
	})
}
export function useOgTreeMapByProductGroupsQuery<
	T = TreeMapProductGroupModel[],
>(
	params: TreeMapRequestModel,
	options?: UseQueryOptions<TreeMapProductGroupModel[], Error, T>
) {
	return useQuery<TreeMapProductGroupModel[], Error, T>({
		...optimizationGroupQueries.treeMapByProductGroups(params),
		...(options || {}),
		staleTime: 60 * 1000 * 60,
	})
}
export function useOgTreeMapBySalesEntitiesQuery<T = TreeMapSalesEntityModel[]>(
	params: TreeMapRequestModel,
	options?: UseQueryOptions<TreeMapSalesEntityModel[], Error, T>
) {
	return useQuery<TreeMapSalesEntityModel[], Error, T>({
		...optimizationGroupQueries.treeMapBySalesEntities(params),
		...(options || {}),
		staleTime: 60 * 1000 * 60,
	})
}
export const useDryCategoriesQuery = <T = CategoryModel[]>(
	options?: UseQueryOptions<CategoryModel[], Error, T>
) =>
	useQuery<CategoryModel[], Error, T>({
		...optimizationGroupQueries.dryCategories(),
		...(options || {}),
	})
